import React, { useEffect, useMemo, useState } from 'react';
import {
  createCustomer,
  createCustomerAdmin,
  createCustomerUser,
  deleteCustomer,
  deleteCustomerAdmin,
  deleteCustomerUser,
  fetchAuditLogs,
  fetchCustomers,
  fetchLicenseUsage,
  fetchCustomerUsers,
  forceLogout,
  listCustomerAdmins,
  renewCustomer,
  updateCustomer,
  updateCustomerAdmin,
  updateCustomerUser,
} from '../services/api';
import CredentialsModal from './CredentialsModal';
import RenewModal from './RenewModal';

const defaultCustomerForm = {
  customer_id: '',
  name: '',
  primary_contact_name: '',
  primary_contact_email: '',
  contact_phone: '',
  status: 'active',
  region: '',
  timezone: '',
  notes: '',
  expiry_date: '',
  max_active_users: 0,
};

const defaultAdminForm = {
  username: '',
  email: '',
  display_name: '',
  expiry_date: '',
  send_email: true,
};

const defaultUserForm = {
  username: '',
  email: '',
  display_name: '',
  status: 'active',
  expiry_date: '',
};

function SuperAdminDashboard({ session, onLogout }) {
  const token = session?.access;
  const [customers, setCustomers] = useState([]);
  const [licenseUsage, setLicenseUsage] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [adminForm, setAdminForm] = useState(defaultAdminForm);
  const [userForm, setUserForm] = useState(defaultUserForm);
  const [customerForm, setCustomerForm] = useState(defaultCustomerForm);
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [customerUsers, setCustomerUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingCustomer, setSavingCustomer] = useState(false);
  const [savingAdmin, setSavingAdmin] = useState(false);
  const [savingUser, setSavingUser] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [credentialsModal, setCredentialsModal] = useState({
    isOpen: false,
    username: '',
    password: '',
    title: 'Credentials Generated',
  });
  const [renewModal, setRenewModal] = useState({
    isOpen: false,
    customerId: null,
    customerName: '',
  });
  const [logsModal, setLogsModal] = useState({
    isOpen: false,
    userId: null,
    username: '',
    logs: [],
    loading: false,
  });

  const selectedCustomer = useMemo(
    () => customers.find((customer) => customer.id === selectedCustomerId) || null,
    [customers, selectedCustomerId]
  );

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 4000);
  };

  const loadCustomers = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchCustomers(token);
      setCustomers(data);
      if (!selectedCustomerId && data.length) {
        setSelectedCustomerId(data[0].id);
      }
    } catch (err) {
      setError(err.message || 'Unable to load customers.');
    } finally {
      setLoading(false);
    }
  };

  const loadLicenseUsage = async () => {
    try {
      const data = await fetchLicenseUsage(token);
      setLicenseUsage(data);
    } catch (err) {
      console.warn('Failed to load license usage', err);
    }
  };

  const loadAudit = async () => {
    try {
      const data = await fetchAuditLogs(token);
      setAuditLogs(data);
    } catch (err) {
      console.warn('Failed to load audit logs', err);
    }
  };

  const loadAdmins = async (customerId) => {
    if (!customerId) return;
    try {
      const data = await listCustomerAdmins(token, customerId);
      setAdmins(data);
    } catch (err) {
      setError(err.message || 'Failed to load customer admins.');
    }
  };

  const loadUsers = async (customerId) => {
    if (!customerId) return;
    try {
      const data = await fetchCustomerUsers(token, customerId);
      setCustomerUsers(data);
    } catch (err) {
      setError(err.message || 'Failed to load customer users.');
    }
  };

  useEffect(() => {
    loadCustomers();
    loadLicenseUsage();
    loadAudit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setError('');
    if (selectedCustomerId) {
      loadAdmins(selectedCustomerId);
      loadUsers(selectedCustomerId);
    } else {
      setAdmins([]);
      setCustomerUsers([]);
    }
  }, [selectedCustomerId]);

  const handleCustomerSelect = (customerId) => {
    setSelectedCustomerId(customerId);
  };

  const handleCustomerFormChange = (event) => {
    const { name, value } = event.target;
    setCustomerForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAdminFormChange = (event) => {
    const { name, value, type, checked } = event.target;
    setAdminForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleUserFormChange = (event) => {
    const { name, value } = event.target;
    setUserForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateCustomer = async (event) => {
    event.preventDefault();
    setSavingCustomer(true);
    try {
      await createCustomer(token, {
        ...customerForm,
        max_active_users: Number(customerForm.max_active_users) || 0,
      });
      showSuccess('Customer created successfully.');
      setCustomerForm(defaultCustomerForm);
      setShowCustomerForm(false);
      await loadCustomers();
    } catch (err) {
      setError(err.message || 'Failed to create customer.');
    } finally {
      setSavingCustomer(false);
    }
  };

  const handleUpdateCustomer = async () => {
    if (!selectedCustomer) return;
    setSavingCustomer(true);
    try {
      const payload = {
        status: selectedCustomer.status,
        expiry_date: selectedCustomer.expiry_date,
        max_active_users: selectedCustomer.max_active_users,
        notes: selectedCustomer.notes,
      };
      await updateCustomer(token, selectedCustomer.id, payload);
      await loadCustomers();
      showSuccess('Customer updated.');
    } catch (err) {
      setError(err.message || 'Failed to update customer.');
    } finally {
      setSavingCustomer(false);
    }
  };

  const handleDeleteCustomer = async (customerId) => {
    if (!window.confirm('Delete this customer? All accounts will lose access.')) return;
    try {
      await deleteCustomer(token, customerId);
      setSelectedCustomerId(null);
      await loadCustomers();
      showSuccess('Customer deleted.');
    } catch (err) {
      setError(err.message || 'Failed to delete customer.');
    }
  };

  const handleCreateAdmin = async (event) => {
    event.preventDefault();
    if (!selectedCustomer) return;
    setSavingAdmin(true);
    try {
      const data = await createCustomerAdmin(token, selectedCustomer.id, adminForm);
      await loadAdmins(selectedCustomer.id);
      
      // Show credentials modal
      if (data.temporary_password && data.username) {
        setCredentialsModal({
          isOpen: true,
          username: data.username,
          password: data.temporary_password,
          title: 'Customer Admin Credentials',
        });
      } else {
        showSuccess('Customer admin created.');
      }
      
      setAdminForm(defaultAdminForm);
    } catch (err) {
      setError(err.message || 'Failed to create customer admin.');
    } finally {
      setSavingAdmin(false);
    }
  };

  const handleUpdateAdmin = async (adminId, payload) => {
    if (!selectedCustomer) return;
    try {
      await updateCustomerAdmin(token, selectedCustomer.id, adminId, payload);
      await loadAdmins(selectedCustomer.id);
      showSuccess('Customer admin updated.');
    } catch (err) {
      setError(err.message || 'Failed to update customer admin.');
    }
  };

  const handleDeleteAdmin = async (adminId) => {
    if (!selectedCustomer) return;
    if (!window.confirm('Delete this customer admin?')) return;
    try {
      await deleteCustomerAdmin(token, selectedCustomer.id, adminId);
      await loadAdmins(selectedCustomer.id);
      showSuccess('Customer admin removed.');
    } catch (err) {
      setError(err.message || 'Failed to delete customer admin.');
    }
  };

  const handleCreateUser = async (event) => {
    event.preventDefault();
    if (!selectedCustomer) return;
    setSavingUser(true);
    try {
      const data = await createCustomerUser(token, selectedCustomer.id, userForm);
      await loadUsers(selectedCustomer.id);
      
      // Show credentials modal
      if (data.temporary_password && data.username) {
        setCredentialsModal({
          isOpen: true,
          username: data.username,
          password: data.temporary_password,
          title: 'Customer User Credentials',
        });
      } else {
        showSuccess('User created.');
      }
      
      setUserForm(defaultUserForm);
    } catch (err) {
      const detail =
        err?.payload?.non_field_errors?.[0] ||
        err?.payload?.status?.[0] ||
        err.message ||
        'Failed to create user.';
      setError(detail);
    } finally {
      setSavingUser(false);
    }
  };

  const handleUpdateUserStatus = async (userId, status) => {
    if (!selectedCustomer) return;
    try {
      await updateCustomerUser(token, selectedCustomer.id, userId, { status });
      await loadUsers(selectedCustomer.id);
      showSuccess('User status updated.');
    } catch (err) {
      const detail =
        err?.payload?.status?.[0] || err.message || 'Failed to update user status.';
      setError(detail);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!selectedCustomer) return;
    if (!window.confirm('Delete this user?')) return;
    try {
      await deleteCustomerUser(token, selectedCustomer.id, userId);
      await loadUsers(selectedCustomer.id);
      showSuccess('User deleted.');
    } catch (err) {
      setError(err.message || 'Failed to delete user.');
    }
  };

  const handleForceLogout = async (accountId) => {
    try {
      await forceLogout(token, accountId);
      showSuccess('User sessions revoked.');
    } catch (err) {
      setError(err.message || 'Failed to force logout.');
    }
  };

  const handleRenewCustomer = async (customerId, expiryDate) => {
    try {
      await renewCustomer(token, customerId, expiryDate);
      await loadCustomers();
      if (selectedCustomerId === customerId) {
        await loadAdmins(customerId);
        await loadUsers(customerId);
      }
      showSuccess('Customer renewed successfully. All admins and users have been updated.');
      setRenewModal({ isOpen: false, customerId: null, customerName: '' });
    } catch (err) {
      const detail =
        err?.payload?.expiry_date?.[0] || err.message || 'Failed to renew customer.';
      setError(detail);
    }
  };

  const selectedUsage = useMemo(() => {
    if (!selectedCustomer) return null;
    const fromUsage = licenseUsage.find((item) => item.id === selectedCustomer.id);
    return fromUsage?.entitlement || selectedCustomer.entitlement_usage;
  }, [selectedCustomer, licenseUsage]);

  const fetchUserQueryLogs = async (userId, username) => {
    setLogsModal({ isOpen: true, userId, username, loading: true, logs: [] });
    
    try {
      const response = await fetch(`/api/users/${userId}/query-logs/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch query logs');
      }
      
      const data = await response.json();
      setLogsModal({ isOpen: true, userId, username, logs: data, loading: false });
    } catch (err) {
      console.error('Error fetching query logs:', err);
      setError('Failed to fetch query logs: ' + err.message);
      setLogsModal({ isOpen: true, userId, username, logs: [], loading: false });
    }
  };

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div>
          <h1>FlashAI User Management Console</h1>
          <p>Signed in as {session?.display_name || session?.profile?.display_name}</p>
        </div>
        <button className="outline-button" onClick={onLogout}>
          Logout
        </button>
      </header>

      {error && <div className="alert error">{error}</div>}
      {successMessage && <div className="alert success">{successMessage}</div>}

      <section className="dashboard-section">
        <div className="section-header">
          <h2>Customers</h2>
          <button
            className="primary-button"
            onClick={() => setShowCustomerForm((prev) => !prev)}
          >
            {showCustomerForm ? 'Close' : 'New Customer'}
          </button>
        </div>
        {showCustomerForm && (
          <form className="card form-grid" onSubmit={handleCreateCustomer}>
            <h3>Create Customer</h3>
            <div className="grid-two">
              <label>
                Customer ID
                <input
                  required
                  name="customer_id"
                  value={customerForm.customer_id}
                  onChange={handleCustomerFormChange}
                />
              </label>
              <label>
                Name
                <input
                  required
                  name="name"
                  value={customerForm.name}
                  onChange={handleCustomerFormChange}
                />
              </label>
            </div>
            <div className="grid-two">
              <label>
                Contact Name
                <input
                  name="primary_contact_name"
                  value={customerForm.primary_contact_name}
                  onChange={handleCustomerFormChange}
                />
              </label>
              <label>
                Contact Email
                <input
                  required
                  type="email"
                  name="primary_contact_email"
                  value={customerForm.primary_contact_email}
                  onChange={handleCustomerFormChange}
                />
              </label>
            </div>
            <div className="grid-two">
              <label>
                Contact Phone
                <input
                  name="contact_phone"
                  value={customerForm.contact_phone}
                  onChange={handleCustomerFormChange}
                />
              </label>
              <label>
                Status
                <select
                  name="status"
                  value={customerForm.status}
                  onChange={handleCustomerFormChange}
                >
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                  <option value="expired">Expired</option>
                </select>
              </label>
            </div>
            <div className="grid-two">
              <label>
                Region
                <input
                  name="region"
                  value={customerForm.region}
                  onChange={handleCustomerFormChange}
                />
              </label>
              <label>
                Timezone
                <input
                  name="timezone"
                  value={customerForm.timezone}
                  onChange={handleCustomerFormChange}
                />
              </label>
            </div>
            <div className="grid-two">
              <label>
                Entitlement (Max Active Users)
                <input
                  type="number"
                  name="max_active_users"
                  min="0"
                  value={customerForm.max_active_users}
                  onChange={handleCustomerFormChange}
                />
              </label>
              <label>
                Expiry Date
                <input
                  type="date"
                  name="expiry_date"
                  value={customerForm.expiry_date}
                  onChange={handleCustomerFormChange}
                />
              </label>
            </div>
            <label>
              Notes
              <textarea
                name="notes"
                value={customerForm.notes}
                onChange={handleCustomerFormChange}
              />
            </label>
            <button className="primary-button" type="submit" disabled={savingCustomer}>
              {savingCustomer ? 'Saving...' : 'Create Customer'}
            </button>
          </form>
        )}

        <div className="card table-container">
          {loading ? (
            <p>Loading customers...</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Entitlement</th>
                  <th>Expiry</th>
                  <th>Available</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {customers.map((customer) => (
                  <tr
                    key={customer.id}
                    className={
                      selectedCustomerId === customer.id ? 'row-selected' : undefined
                    }
                  >
                    <td>
                      <button
                        className="link-button"
                        onClick={() => handleCustomerSelect(customer.id)}
                      >
                        {customer.name}
                      </button>
                    </td>
                    <td className={`status-${customer.status}`}>{customer.status}</td>
                    <td>{customer.entitlement_usage?.allowed ?? customer.max_active_users}</td>
                    <td>{customer.expiry_date || '—'}</td>
                    <td>{customer.entitlement_usage?.available ?? '—'}</td>
                    <td>
                      {customer.status === 'expired' && (
                        <button
                          className="primary-button"
                          onClick={() =>
                            setRenewModal({
                              isOpen: true,
                              customerId: customer.id,
                              customerName: customer.name,
                            })
                          }
                          style={{ marginRight: '0.5rem' }}
                        >
                          Renew
                        </button>
                      )}
                      <button
                        className="danger-button"
                        onClick={() => handleDeleteCustomer(customer.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {selectedCustomer && (
        <>
          <section className="dashboard-section">
            <div className="card">
              <h2>{selectedCustomer.name}</h2>
              <div className="grid-two">
                <label>
                  Status
                  <select
                    value={selectedCustomer.status}
                    onChange={(event) =>
                      setCustomers((prev) =>
                        prev.map((customer) =>
                          customer.id === selectedCustomer.id
                            ? { ...customer, status: event.target.value }
                            : customer
                        )
                      )
                    }
                  >
                    <option value="active">Active</option>
                    <option value="suspended">Suspended</option>
                    <option value="expired">Expired</option>
                  </select>
                </label>
                <label>
                  Entitlement (Active Users)
                  <input
                    type="number"
                    min="0"
                    value={selectedCustomer.max_active_users}
                    onChange={(event) =>
                      setCustomers((prev) =>
                        prev.map((customer) =>
                          customer.id === selectedCustomer.id
                            ? { ...customer, max_active_users: Number(event.target.value) }
                            : customer
                        )
                      )
                    }
                  />
                </label>
              </div>
              <div className="grid-two">
                <label>
                  Expiry Date
                  <input
                    type="date"
                    value={selectedCustomer.expiry_date || ''}
                    onChange={(event) =>
                      setCustomers((prev) =>
                        prev.map((customer) =>
                          customer.id === selectedCustomer.id
                            ? { ...customer, expiry_date: event.target.value }
                            : customer
                        )
                      )
                    }
                  />
                </label>
                <label>
                  Notes
                  <textarea
                    value={selectedCustomer.notes || ''}
                    onChange={(event) =>
                      setCustomers((prev) =>
                        prev.map((customer) =>
                          customer.id === selectedCustomer.id
                            ? { ...customer, notes: event.target.value }
                            : customer
                        )
                      )
                    }
                  />
                </label>
              </div>
              {selectedUsage && (
                <div className="metrics">
                  <div>
                    <span>Allowed</span>
                    <strong>{selectedUsage.allowed}</strong>
                  </div>
                  <div>
                    <span>Active</span>
                    <strong>{selectedUsage.active}</strong>
                  </div>
                  <div>
                    <span>Available</span>
                    <strong>{selectedUsage.available}</strong>
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  className="primary-button"
                  onClick={handleUpdateCustomer}
                  disabled={savingCustomer}
                >
                  {savingCustomer ? 'Saving...' : 'Save Changes'}
                </button>
                {selectedCustomer.status === 'expired' && (
                  <button
                    className="secondary-button"
                    onClick={() =>
                      setRenewModal({
                        isOpen: true,
                        customerId: selectedCustomer.id,
                        customerName: selectedCustomer.name,
                      })
                    }
                  >
                    Renew Customer
                  </button>
                )}
              </div>
            </div>
          </section>

          <section className="dashboard-section two-column">
            <div className="card">
              <h3>Customer Admins</h3>
              <form className="stacked-form" onSubmit={handleCreateAdmin}>
                <div className="grid-two">
                  <label>
                    Username
                    <input
                      required
                      name="username"
                      value={adminForm.username}
                      onChange={handleAdminFormChange}
                    />
                  </label>
                  <label>
                    Email
                    <input
                      type="email"
                      required
                      name="email"
                      value={adminForm.email}
                      onChange={handleAdminFormChange}
                    />
                  </label>
                </div>
                <div className="grid-two">
                  <label>
                    Display Name
                    <input
                      name="display_name"
                      value={adminForm.display_name}
                      onChange={handleAdminFormChange}
                    />
                  </label>
                  <label>
                    Expiry
                    <input
                      type="date"
                      name="expiry_date"
                      value={adminForm.expiry_date}
                      onChange={handleAdminFormChange}
                    />
                  </label>
                </div>
                <label className="checkbox">
                  <input
                    type="checkbox"
                    name="send_email"
                    checked={adminForm.send_email}
                    onChange={handleAdminFormChange}
                  />
                  Email credentials to contact
                </label>
                <button className="secondary-button" type="submit" disabled={savingAdmin}>
                  {savingAdmin ? 'Creating...' : 'Create Admin'}
                </button>
              </form>

              <table className="compact-table">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Expiry</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {admins.map((admin) => (
                    <tr key={admin.id}>
                      <td>{admin.username}</td>
                      <td>{admin.email}</td>
                      <td className={`status-${admin.status}`}>{admin.status}</td>
                      <td>{admin.expiry_date || '—'}</td>
                      <td>
                        <button
                          className="inline-button"
                          onClick={() =>
                            handleUpdateAdmin(admin.id, {
                              status: admin.status === 'active' ? 'inactive' : 'active',
                            })
                          }
                        >
                          {admin.status === 'active' ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          className="inline-button"
                          onClick={() => handleDeleteAdmin(admin.id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="card">
              <h3>Customer Users</h3>
              <form className="stacked-form" onSubmit={handleCreateUser}>
                <div className="grid-two">
                  <label>
                    Username
                    <input
                      required
                      name="username"
                      value={userForm.username}
                      onChange={handleUserFormChange}
                    />
                  </label>
                  <label>
                    Email
                    <input
                      type="email"
                      name="email"
                      value={userForm.email}
                      onChange={handleUserFormChange}
                    />
                  </label>
                </div>
                <div className="grid-two">
                  <label>
                    Display Name
                    <input
                      name="display_name"
                      value={userForm.display_name}
                      onChange={handleUserFormChange}
                    />
                  </label>
                  <label>
                    Expiry
                    <input
                      type="date"
                      name="expiry_date"
                      value={userForm.expiry_date}
                      onChange={handleUserFormChange}
                    />
                  </label>
                </div>
                <label>
                  Status
                  <select
                    name="status"
                    value={userForm.status}
                    onChange={handleUserFormChange}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </label>
                <button className="secondary-button" type="submit" disabled={savingUser}>
                  {savingUser ? 'Saving...' : 'Create User'}
                </button>
              </form>

              <table className="compact-table">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Expiry</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {customerUsers.map((user) => (
                    <tr key={user.id}>
                      <td>{user.username}</td>
                      <td>{user.email || '—'}</td>
                      <td className={`status-${user.status}`}>{user.status}</td>
                      <td>{user.expiry_date || '—'}</td>
                      <td>
                        <button
                          className="inline-button"
                          onClick={() => fetchUserQueryLogs(user.id, user.username)}
                        >
                          Logs
                        </button>
                        <button
                          className="inline-button"
                          onClick={() =>
                            handleUpdateUserStatus(
                              user.id,
                              user.status === 'active' ? 'inactive' : 'active'
                            )
                          }
                        >
                          {user.status === 'active' ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          className="inline-button"
                          onClick={() => handleForceLogout(user.id)}
                        >
                          Force Logout
                        </button>
                        <button
                          className="inline-button"
                          onClick={() => handleDeleteUser(user.id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      <section className="dashboard-section">
        <div className="card">
          <h3>License Usage Overview</h3>
          <div className="metrics-grid">
            {licenseUsage.map((item) => (
              <div key={item.id} className="metric-card">
                <span>{item.name}</span>
                <strong>
                  {item.entitlement.active}/{item.entitlement.allowed}
                </strong>
                <small>{item.entitlement.available} slots available</small>
              </div>
            ))}
            {licenseUsage.length === 0 && <p>No license data yet.</p>}
          </div>
        </div>
      </section>

      <section className="dashboard-section">
        <div className="card">
          <h3>Recent Audit Logs</h3>
          <div className="audit-log">
            {auditLogs.slice(0, 10).map((log) => (
              <div key={log.id} className="audit-log-item">
                <div>
                  <strong>{log.action}</strong> on <span>{log.target_type}</span>
                  <small> {new Date(log.timestamp).toLocaleString()}</small>
                </div>
                <small>Actor: {log.actor_username || 'System'}</small>
              </div>
            ))}
            {auditLogs.length === 0 && <p>No audit events yet.</p>}
          </div>
        </div>
      </section>
      
      <CredentialsModal
        isOpen={credentialsModal.isOpen}
        onClose={() => setCredentialsModal({ ...credentialsModal, isOpen: false })}
        username={credentialsModal.username}
        password={credentialsModal.password}
        title={credentialsModal.title}
      />

      <RenewModal
        isOpen={renewModal.isOpen}
        onClose={() => setRenewModal({ isOpen: false, customerId: null, customerName: '' })}
        onConfirm={(expiryDate) => handleRenewCustomer(renewModal.customerId, expiryDate)}
        customerName={renewModal.customerName}
      />

      {/* Query Logs Modal */}
      {logsModal.isOpen && (
        <div className="modal-overlay" onClick={() => setLogsModal({ ...logsModal, isOpen: false })}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1200px', maxHeight: '80vh', overflow: 'auto' }}>
            <div className="modal-header">
              <h2>Query Logs - {logsModal.username}</h2>
              <button
                className="close-button"
                onClick={() => setLogsModal({ ...logsModal, isOpen: false })}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              {logsModal.loading ? (
                <p>Loading logs...</p>
              ) : logsModal.logs.length === 0 ? (
                <p>No query logs found for this user.</p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="compact-table">
                    <thead>
                      <tr>
                        <th>Timestamp</th>
                        <th>Query</th>
                        <th>Execution Time (ms)</th>
                        <th>Status</th>
                        <th>Rows Returned</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logsModal.logs.map((log) => (
                        <tr key={log.id} className={log.status === 'error' ? 'error-row' : ''}>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            {new Date(log.executed_at).toLocaleString()}
                          </td>
                          <td>
                            <div style={{ 
                              maxWidth: '400px', 
                              maxHeight: '100px', 
                              overflow: 'auto', 
                              fontSize: '0.85em',
                              fontFamily: 'monospace',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word'
                            }}>
                              {log.query}
                            </div>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            {log.execution_time_ms.toFixed(2)}
                          </td>
                          <td>
                            <span className={`status-${log.status}`}>
                              {log.status}
                            </span>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            {log.result_row_count !== null ? log.result_row_count : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            
            <div className="modal-footer">
              <button
                className="secondary-button"
                onClick={() => setLogsModal({ ...logsModal, isOpen: false })}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SuperAdminDashboard;

